import type { ReusableComponentType } from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as reuseApi from "@/lib/api/flow-instances/reuse-previous-answer";
import { reuseKeys } from "./reuse-keys";
import { clientClassificationKeys } from "./use-client-classification";
import { useInvalidateFlowCompletion } from "./use-flow-completion";
import { invalidateSuitabilityQueries } from "./use-goals";
import { useInvalidateDynamicSync } from "./use-portfolios";
import { productAssessmentKeys } from "./use-product-assessment";
import { sumsubVerificationKeys } from "./use-sumsub-verification";

// Map the wire `componentType` (snake_case) to the existing per-component value-cache key factory
// (kebab-case), so the apply mutation can update the same cache the save mutation writes.
const valueKeyByType: Record<ReusableComponentType, (id: string | null) => readonly unknown[]> = {
  client_classification: clientClassificationKeys.flowInstanceValue,
  product_assessment: productAssessmentKeys.flowInstanceValue,
  sumsub_verification: sumsubVerificationKeys.flowInstanceValue,
};

/** QL-505 — `subjectId` addresses one party when several answer (a group member, or a representative when 2+ are registered). */
export function useReuseAvailability(
  instanceId: string | null,
  componentType: ReusableComponentType,
  subjectId?: string | null,
) {
  const { data, isLoading } = useQuery({
    queryKey: reuseKeys.availability(instanceId, componentType, subjectId),
    queryFn: () => reuseApi.getAvailability(instanceId!, componentType, subjectId ?? undefined),
    enabled: !!instanceId,
  });
  return {
    available: data?.available ?? false,
    sourceDate: data?.sourceDate ?? null,
    isLoading,
  };
}

/** QL-505 — `subjectId` addresses one party when several answer (a group member, or a representative when 2+ are registered). */
export function useApplyPreviousAnswer(
  instanceId: string,
  componentType: ReusableComponentType,
  subjectId?: string | null,
) {
  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();
  const invalidateDynamicSync = useInvalidateDynamicSync();
  return useMutation({
    mutationFn: () =>
      reuseApi.applyPreviousAnswer(instanceId, componentType, subjectId ?? undefined),
    onSuccess: (res) => {
      // Update the component-value cache from the returned value — no component refetch. This is
      // the same cache the save mutation writes; the widget's value-sync effect converges from it.
      // QL-505 — members mode carries the whole updated blob (server-merged), so this still holds
      // even though the request was scoped to one member.
      queryClient.setQueryData(valueKeyByType[componentType](instanceId), {
        inputData: res.value.inputData,
        calculatedData: res.value.calculatedData,
      });
      // Mirror the save mutation's downstream side-effects. (For sumsub, the terminal-state
      // completion invalidation in useSumsubVerificationFlowInstanceValue also fires off the
      // setQueryData above, so it is preserved.)
      invalidateCompletion(instanceId);
      queryClient.invalidateQueries({
        queryKey: reuseKeys.availability(instanceId, componentType, subjectId),
      });
      invalidateSuitabilityQueries(queryClient);
      // The applied answer may be a generator input — the apply regenerated dynamic portfolios.
      invalidateDynamicSync(res.dynamicSync);
    },
    onError: () => {
      // Completed-in-another-tab race (or any refusal): refetch availability so the button
      // reflects server truth and hides.
      queryClient.invalidateQueries({
        queryKey: reuseKeys.availability(instanceId, componentType, subjectId),
      });
    },
  });
}
